/**
 * @file useDealsBoard.ts
 * @description Данные доски сделок (phase09; phase19.31 — вынесено из
 * страницы): воронки, выбранная воронка (в адресе `?pipelineId=`), её
 * активные стадии и сделки, перенос сделки между стадиями.
 *
 * Перенос — оптимистичный: карточка сразу встаёт в новую колонку, ответ
 * сервера заменяет её свежей сделкой; ошибка — откат и уведомление
 * человеческим текстом. Перетаскивание работает как раньше.
 *
 * Перенос в «Не состоялась» / «Отменена» (phase19.31, задача 3): сервер
 * требует причину (`lost_reason_required`), поэтому карточка не двигается
 * сразу — открывается окно причины (`pendingMove`); «Отмена» оставляет её
 * в прежней колонке, «Перенести» ставит свежую сделку с сервера. Если сервер
 * всё равно ответил «нужна причина» (вид стадии поменяли в настройках), —
 * откат и то же окно.
 */

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  listDeals,
  listPipelines,
  listStages,
  moveDealToStage,
  type DealDto,
  type PipelineDto,
  type PipelineStageDto,
} from "../../../lib/api-crm";
import { dealMoveErrorText, isLostReasonRequired, needsLostReason } from "../../../lib/crm-deal-move";

/** Перенос, который ждёт причины в окне. */
export interface PendingDealMove {
  deal: DealDto;
  stageId: string;
}

export interface DealsBoardState {
  pipelines: PipelineDto[];
  pipelineId: string;
  setPipelineId: (id: string) => void;
  stages: PipelineStageDto[];
  deals: DealDto[];
  loading: boolean;
  error: string | null;
  activeDeal: DealDto | null;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  reload: () => void;
  /** Перенос в стадию с обязательной причиной — окно открыто. */
  pendingMove: PendingDealMove | null;
  /** «Отмена» в окне причины: карточка остаётся где была. */
  cancelMove: () => void;
  /** Сервер перенёс сделку из окна причины. */
  completeMove: (updated: DealDto) => void;
}

export function useDealsBoard(): DealsBoardState {
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const [pipelines, setPipelines] = useState<PipelineDto[]>([]);
  const [stages, setStages] = useState<PipelineStageDto[]>([]);
  const [deals, setDeals] = useState<DealDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDeal, setActiveDeal] = useState<DealDto | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [pendingMove, setPendingMove] = useState<PendingDealMove | null>(null);
  const dealsBeforeDrag = useRef<DealDto[]>([]);

  const requested = params.get("pipelineId") ?? "";
  // Нет воронки в адресе — воронка по умолчанию (или первая).
  const pipelineId = requested || (pipelines.find((p) => p.isDefault) ?? pipelines[0])?.id || "";

  const setPipelineId = useCallback(
    (id: string) => setParams({ pipelineId: id }, { replace: true }),
    [setParams],
  );

  // Воронки перечитываем и по «Обновить»/«Повторить»: после сбоя их загрузки
  // повтор должен поднять и список воронок, а не только колонки.
  useEffect(() => {
    listPipelines()
      .then((res) => {
        setPipelines(res.items);
        if (res.items.length === 0) setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить воронки");
        setLoading(false);
      });
  }, [attempt]);

  useEffect(() => {
    if (!pipelineId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listStages(pipelineId), listDeals({ pipelineId, limit: 200 })])
      .then(([s, d]) => {
        if (cancelled) return;
        setStages(s.filter((stage) => stage.isActive));
        setDeals(d.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить доску");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pipelineId, attempt]);

  const onDragStart = useCallback(
    (event: DragStartEvent) => setActiveDeal(deals.find((d) => d.id === event.active.id) ?? null),
    [deals],
  );

  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDeal(null);
      if (!over) return;
      const dealId = String(active.id);
      const overData = over.data.current as { type: string; stageId?: string } | undefined;
      const targetStageId =
        overData?.type === "stage" ? overData.stageId : deals.find((d) => d.id === over.id)?.currentStageId;
      const deal = deals.find((d) => d.id === dealId);
      if (!targetStageId || !deal || deal.currentStageId === targetStageId) return;
      const targetStage = stages.find((s) => s.id === targetStageId);
      if (targetStage && needsLostReason(targetStage.kind)) {
        setPendingMove({ deal, stageId: targetStageId });
        return;
      }

      dealsBeforeDrag.current = deals;
      setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, currentStageId: targetStageId } : d)));
      try {
        const updated = await moveDealToStage(dealId, { toStageId: targetStageId });
        setDeals((prev) => prev.map((d) => (d.id === dealId ? updated : d)));
      } catch (err) {
        setDeals(dealsBeforeDrag.current);
        if (isLostReasonRequired(err)) setPendingMove({ deal, stageId: targetStageId });
        else notify.error(dealMoveErrorText(err));
      }
    },
    [deals, stages, notify],
  );

  const cancelMove = useCallback(() => setPendingMove(null), []);
  const completeMove = useCallback((updated: DealDto) => {
    setPendingMove(null);
    setDeals((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  }, []);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    pipelines,
    pipelineId,
    setPipelineId,
    stages,
    deals,
    loading,
    error,
    activeDeal,
    onDragStart,
    onDragEnd: (event) => void onDragEnd(event),
    reload,
    pendingMove,
    cancelMove,
    completeMove,
  };
}
